import { CreationToolError } from '@App/app/shared/exceptions/creation-tool-error';
import { Injectable } from '@angular/core';
import { AbstractMesh, Matrix, Plane, Quaternion, Vector3 } from 'babylonjs';
import { BabylonNodesService } from '../../babylon-nodes-service/babylon-nodes.service';
import { AngleIronMathService } from './angle-iron-math.service';
import { AngleIronRaycastService } from './angle-iron-raycast.service';

export interface AngleIronGeometryResult {
  boxes: { min: Vector3; max: Vector3 }[];
  quaternion: Quaternion;
  length: number;
}

@Injectable({
  providedIn: 'root',
})
export class AngleIronGeometryService {
  constructor(
    private mathService: AngleIronMathService,
    private raycastService: AngleIronRaycastService,
    private nodeService: BabylonNodesService,
  ) {}

  calculateAngleIronData(
    samples: [AbstractMesh, AbstractMesh],
    planes: [Plane, Plane],
  ): AngleIronGeometryResult {
    const centers = samples.map((sample, i) =>
      this.mathService.findCenterPoint(sample.position, planes[i]),
    ) as [Vector3, Vector3];
    const normals = this.mathService.getNormals(centers, planes);
    const cross = Vector3.Cross(normals[0], normals[1]);
    const orthoPlanes: [Plane, Plane] = [
      planes[0],
      Plane.FromPositionAndNormal(
        centers[1].projectOnPlane(planes[1], centers[1].add(planes[1].normal)),
        Vector3.Cross(normals[0], cross.negate()),
      ),
    ];
    const orthoNormals = this.mathService.getNormals(centers, orthoPlanes);
    const lengthEdges = this.raycastService.findLengthEdges(centers, orthoPlanes, cross);
    const length = Vector3.Distance(lengthEdges[0], lengthEdges[1]);
    if (length <= 0) {
      throw new CreationToolError('Length edges not valid');
    }
    const edgePlane = Plane.FromPositionAndNormal(lengthEdges[0], cross.negate());
    const projected = this.mathService.findProjectedEdges(centers, orthoPlanes);
    const quaternion = Quaternion.FromRotationMatrix(
      Matrix.LookDirectionLH(cross, orthoNormals[0].negate()),
    );
    const thickness = this.raycastService.findThickness(
      centers,
      projected,
      orthoNormals,
      orthoPlanes,
      cross,
    );

    const boxes = centers.map((center, i) => {
      const pick = this.raycastService.findBoxEdgePoint(center, normals[Math.abs(i - 1)]);
      const projectedPick = this.mathService.doubleProjectOnPlanes(pick, [
        orthoPlanes[i],
        Plane.FromPositionAndNormal(projected[i], cross),
      ]);
      const thicknessEdge = projected[Math.abs(i - 1)].add(normals[i].scale(thickness));
      const { min, max } = this.mathService.minMaxVectors([projectedPick, thicknessEdge]);
      const position = Vector3.Center(min, max);
      const projectedPosition = position.projectOnPlane(edgePlane, position.add(cross));
      const centeredPosition = projectedPosition.subtract(cross.scale(length / 2));

      const box = this.nodeService.createBox(centeredPosition, {
        width: i === 1 ? thickness : Vector3.Distance(projected[0], projectedPick),
        height: i === 0 ? thickness : Vector3.Distance(projected[1], projectedPick),
        depth: length,
      });
      const boxData = box.getHierarchyBoundingVectors();
      box.dispose();
      return boxData;
    });

    return {
      boxes,
      quaternion,
      length,
    };
  }
}
